import { useState } from "react";

import { GqlPermissions } from "~/__generated__";
import useHasTenantPermission from "~/modules/auth/hooks/useHasPermission";
import { GaiaMap } from "~/modules/map/components/GaiaMap";
import { GaiaMapUIOverlay } from "~/modules/map/components/GaiaMapUIOverlay";
import { useMapDraw } from "~/modules/map/hooks/useMapDraw";
import { MapEditorLayerFragment } from "~/modules/mapEditor/api/MapEditorLayer.fragment.generated";

import { MapButtonFitBounds } from "../../../../../map/components/MapButtonFitBounds";
import { MapButtonToggleBaseMap } from "../../../../../map/components/MapButtonToggleBaseMap";
import MapEditorFeatureCreateButton from "../../../../../mapEditor/components/MapEditorFeatureCreateButton";
import EditorMapLayers from "../../../../../mapEditor/components/MapEditorMapView/EditorMapLayers";
import { useMapEditorDrawState } from "../../../../../mapEditor/hooks/useMapEditorDrawState";
import { useMapEditorFeatureHover } from "../../../../../mapEditor/hooks/useMapEditorFeatureHover";
import { useMapEditorSelection } from "../../../../../mapEditor/hooks/useMapEditorSelection";

type Props = {
    projectId: string;
    selectedLayerId?: string;
    selectedFeatureId?: string;
    onSelect: (layerId?: string, featureId?: string) => void;
    visitId?: string;
    allLayerData?: MapEditorLayerFragment[];
    rightPanel?: React.ReactNode;
    leftPanel?: React.ReactNode;
    allowedDrawTypes?: Array<"area" | "observation" | "ecologicalFunction">;
};

export function ProjectMapEditorMapView({
    projectId,
    selectedLayerId,
    selectedFeatureId,
    onSelect,
    visitId,
    allLayerData,
    rightPanel,
    leftPanel,
    allowedDrawTypes,
}: Props) {
    const isAdmin = useHasTenantPermission(GqlPermissions.Administrator);
    const [map, setMap] = useState<mapboxgl.Map | null>(null);
    const hovering = useMapEditorFeatureHover();

    const { selectedLayer, selectedFeature, mapBounds, adjustedMapPadding } = useMapEditorSelection({
        allLayerData: allLayerData,
        selectedLayerId,
        selectedFeatureId,
    });

    const { drawMode, setDrawMode, featureCreateOption, setFeatureCreateOption, onDeselect, onFeatureCreate, onFeatureUpdate } =
        useMapEditorDrawState({
            selectedLayerId,
            selectedFeature,
            projectId,
            visitId,
            onSelect,
        });

    useMapDraw({
        map,
        enabled: isAdmin,
        selectedFeatureId,
        selectedFeature,
        onDeselect,
        onFeatureCreate,
        onFeatureUpdate,
        selectedDrawMode: drawMode,
    });

    return (
        <>
            {allLayerData && (
                <GaiaMap
                    sx={{ height: "100%", width: "100%" }}
                    padding={adjustedMapPadding}
                    bounds={mapBounds.bbox}
                    onMapLoaded={async loadedMap => setMap(loadedMap)}
                >
                    <GaiaMapUIOverlay
                        leftPanel={leftPanel}
                        topBar={
                            isAdmin &&
                            selectedLayer && (
                                <MapEditorFeatureCreateButton
                                    selectedOption={featureCreateOption}
                                    setSelectedOption={setFeatureCreateOption}
                                    currentDrawMode={drawMode}
                                    onDrawModeChange={setDrawMode}
                                    allowedDrawTypes={allowedDrawTypes}
                                />
                            )
                        }
                        bottomBar={
                            <>
                                <MapButtonFitBounds bounds={mapBounds} padding={adjustedMapPadding} />
                                <MapButtonToggleBaseMap />
                            </>
                        }
                        rightPanel={rightPanel}
                    />
                    {allLayerData.map(layer => (
                        <EditorMapLayers
                            key={layer.id}
                            selectable={drawMode === "simple_select" || drawMode === "direct_select" || drawMode === "static"}
                            layer={layer}
                            selectedFeatureId={selectedFeatureId}
                            selectedLayerId={selectedLayerId}
                            hoveredFeatureId={hovering.mapEditorFeatureId}
                            onSelect={onSelect}
                        />
                    ))}
                </GaiaMap>
            )}
        </>
    );
}
